import {
  ChartLine,
  CreditCard,
  CurrencyCircleDollar,
  House,
  SignOut,
  Users,
} from "@phosphor-icons/react";
import { Button, Separator } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router";

import { Icon } from "@/client/components/icon";
import { adminLogout, getAdminProfile } from "@/client/services/admin";

type SidebarProps = {
  setOpen?: (open: boolean) => void;
};

const ActiveIndicator = ({ className }: { className?: string }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className={cn(
      "size-1.5 animate-pulse rounded-full bg-info shadow-[0_0_12px] shadow-info",
      className,
    )}
  />
);

type SidebarItemProps = {
  path: string;
  name: string;
  icon: React.ReactNode;
  onClick?: () => void;
};

const SidebarItem = ({ path, name, icon, onClick }: SidebarItemProps) => {
  const isActive = useLocation().pathname === path;

  return (
    <Button
      asChild
      size="lg"
      variant="ghost"
      className={cn(
        "h-auto justify-start px-4 py-3",
        isActive && "pointer-events-none bg-secondary/50 text-secondary-foreground",
      )}
      onClick={onClick}
    >
      <Link to={path}>
        <div className="mr-3">{icon}</div>
        <span>{name}</span>
        {isActive && <ActiveIndicator className="ml-auto" />}
      </Link>
    </Button>
  );
};

export const AdminSidebar = ({ setOpen }: SidebarProps) => {
  const admin = getAdminProfile();

  const items: SidebarItemProps[] = [
    { path: "/admin", name: "Dashboard", icon: <ChartLine /> },
    { path: "/admin/plans", name: "Plans", icon: <CurrencyCircleDollar /> },
    { path: "/admin/payments", name: "Payments", icon: <CreditCard /> },
    { path: "/admin/users", name: "Users", icon: <Users /> },
    { path: "/admin/subscriptions", name: "Subscriptions", icon: <CreditCard /> },
  ];

  return (
    <div className="flex h-full flex-col gap-y-4">
      <div className="ml-12 flex justify-center lg:ml-0">
        <Button asChild size="icon" variant="ghost" className="size-25 p-0">
          <Link to="/admin" onClick={() => setOpen?.(false)}>
            <Icon size={100} className="mx-auto hidden lg:block" />
          </Link>
        </Button>
      </div>

      <Separator className="opacity-50" />

      <div className="grid gap-y-2">
        {items.map((item) => (
          <SidebarItem
            key={item.path}
            path={item.path}
            name={item.name}
            icon={item.icon}
            onClick={() => setOpen?.(false)}
          />
        ))}
      </div>

      <div className="flex-1" />

      <Separator className="opacity-50" />

      <div className="grid gap-y-2">
        <Button asChild variant="ghost" size="lg" className="h-auto justify-start px-4 py-3">
          <Link to="/" onClick={() => setOpen?.(false)}>
            <House className="mr-3 size-5" />
            Back to site
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="lg"
          className="h-auto w-full justify-start px-4 py-3"
          onClick={() => {
            adminLogout();
          }}
        >
          <SignOut className="mr-3 size-5" />
          Logout
        </Button>
      </div>

      {admin && <p className="truncate px-4 text-xs text-muted-foreground">{admin.email}</p>}
    </div>
  );
};
